import { useEffect, useMemo, useState } from 'react'

import { DataGridPro as DataGrid } from '@mui/x-data-grid-pro'
import { LicenseInfo } from '@mui/x-license-pro'
import MuiStack from '@mui/material/Stack'
import IconButton from '@mui/material/IconButton'
import EditNoteIcon from '@mui/icons-material/EditNote'
import DeleteIcon from '@mui/icons-material/Delete'

import AppConfig from '../../../../../config'
import { useGetFieldsQuery } from '../../../../../services/fields/slice'

LicenseInfo.setLicenseKey(AppConfig.mui.licenseKey)

const SectionFields = ({ section, setSelectedSection, setFieldToEdit, setFieldToDelete }) => {
  const [rows, setRows] = useState([])
  const { data: fieldColumns, isLoading: isFieldsLoading } = useGetFieldsQuery(section?.entity, {
    skip: !section?.entity,
  })

  useEffect(() => {
    if (section?.fields?.length) {
      const mappedFields = section.fields.map((field) => {
        const fieldColumn = fieldColumns?.find((column) => column.name === field.name)
        return {
          ...field,
          label: fieldColumn?.label || field.name,
          description: fieldColumn?.description || '',
        }
      })
      setRows(mappedFields)
    }
  }, [section, fieldColumns])

  const columns = useMemo(() => {
    const cols = [
      {
        field: 'label',
        headerName: 'Label',
        width: 200,
        description: 'The label that will be displayed on the form',
      },
      {
        field: 'description',
        headerName: 'Description',
        width: 350,
        description: 'A short description of the chosen field',
      },
      {
        field: 'name',
        headerName: 'Name',
        width: 200,
        description: 'The name of the field that will be used in the database',
      },
      {
        field: 'priority',
        headerName: 'Priority',
        width: 100,
        description: 'The order in which the field will be displayed on the form',
      },
      {
        field: 'isReadOnly',
        headerName: 'Read Only',
        width: 100,
        description: 'Whether or not the field can be modified by the BDR',
        type: 'boolean',
      },
    ]
    return [
      ...cols,
      {
        field: 'actions',
        headerName: '',
        width: 100,
        sortable: false,
        disableClickEventBubbling: true,

        renderCell: (params) => {
          const onEditClick = (field) => {
            setSelectedSection(section)
            setFieldToEdit(field)
          }
          const onDeleteClick = (field) => {
            setSelectedSection(section)
            setFieldToDelete(field)
          }

          return (
            <>
              {!params?.row?.isSystemOnly && (
                <MuiStack direction="row">
                  <IconButton size="small" onClick={() => onEditClick(params?.row)}>
                    <EditNoteIcon />
                  </IconButton>
                  <IconButton size="small" onClick={() => onDeleteClick(params?.row)}>
                    <DeleteIcon />
                  </IconButton>
                </MuiStack>
              )}
            </>
          )
        },
      },
    ]
  }, [section, setSelectedSection, setFieldToEdit, setFieldToDelete])

  return (
    <DataGrid
      rows={rows}
      columns={columns}
      density="compact"
      pagination
      loading={isFieldsLoading}
      initialState={{
        pagination: {
          paginationModel: {
            pageSize: 25,
          },
        },
      }}
      pageSizeOptions={[25, 50, 100]}
      disableRowSelectionOnClick
      sx={{
        '& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus': {
          outline: 'none !important',
        },
        '& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-columnHeader:focus': {
          outline: 'none !important',
        },
      }}
    />
  )
}

export default SectionFields
